
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Lost Wax: Gift details and linked wrapping service.
        const giftToggle = this.form.querySelector(
          '.product-form__input--gift-toggle input[type="checkbox"]'
        );

        const isGift = Boolean(giftToggle?.checked);

        const selectedWrap = this.form.querySelector(
          '.lost-wax-gift-options input[name="gift-wrapping-choice"]:checked'
        );

        const senderInput = this.form.querySelector(
          'input[name="properties[Gift from]"]'
        );

        const anonymousInput = this.form.querySelector(
          'input[name="properties[Send anonymously]"]'
        );

        const giftMessageInput = this.form.querySelector(
          'textarea[name="properties[Gift message]"]'
        );

        const isAnonymous =
          isGift && Boolean(anonymousInput?.checked);

        const wrappingVariantId =
          isGift && selectedWrap?.value
            ? Number(selectedWrap.value)
            : null;

        // Gather ordinary product properties, excluding gift-only fields.
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);

          if (!match || value === '') continue;

          const propertyName = match[1];

          if (
            propertyName === 'Gift from' ||
            propertyName === 'Send anonymously' ||
            propertyName === 'Gift message' ||
            propertyName === 'Gift wrapping' ||
            propertyName === 'Gift receipt' ||
            propertyName === 'Gift order'
          ) {
            continue;
          }

          properties[propertyName] = value;
        }

        if (isGift) {
          properties['Gift order'] = 'Yes';

          const giftMessage = giftMessageInput?.value.trim();
          const senderName = senderInput?.value.trim();

          if (giftMessage) {
            properties['Gift message'] = giftMessage;
          }

          if (isAnonymous) {
            properties['Send anonymously'] = 'Yes';
          } else if (senderName) {
            properties['Gift from'] = senderName;
          }

          if (wrappingVariantId) {
            properties['Gift wrapping'] =
              selectedWrap.closest('label')
                ?.querySelector(
                  '.lost-wax-gift-choice__details strong'
                )
                ?.textContent.trim() || 'Gift wrapping';
          }
        }

        // Use a clean FormData object for ordinary purchases
        // and gifts without a paid wrapping service.
        for (const name of Array.from(formData.keys())) {
          if (/^properties\[.+\]$/.test(name)) {
            formData.delete(name);
          }
        }

        for (const [name, value] of Object.entries(properties)) {
          formData.append(`properties[${name}]`, value);
        }

        const sections = this.cart
          ? this.cart.getSectionsToRender().map(
              (section) => section.id
            )
          : [];

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (wrappingVariantId) {
          const productVariantId = Number(formData.get('id'));
          const quantity = Number(formData.get('quantity') || 1);

          const parentItem = {
            id: productVariantId,
            quantity,
            properties
          };

          const sellingPlan = formData.get('selling_plan');

          if (sellingPlan) {
            parentItem.selling_plan = Number(sellingPlan);
          }

          const payload = {
            items: [
              parentItem,
              {
                id: wrappingVariantId,
                quantity,
                parent_id: productVariantId
              }
            ]
          };

          if (this.cart) {
            payload.sections = sections;
            payload.sections_url = window.location.pathname;
          }

          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        } else {
          if (this.cart) {
            formData.append('sections', sections);
            formData.append(
              'sections_url',
              window.location.pathname
            );
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (!soldOutMessage) return;

              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker(
                'add:wait-for-subscribers'
              );

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              CartPerformance.measure(
                'add:paint-updated-sections',
                () => {
                  this.cart.renderContents(response);
                }
              );
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(
              'Something went wrong. Please try again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (
              this.cart &&
              this.cart.classList.contains('is-empty')
            ) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            this.querySelector('.loading__spinner')
              .classList.add('hidden');

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
